<template>
  <div class="ForumResponseEditor">
    <span
      v-if="!localResponse.id && !localResponse.parentResponseId"
      class="ForumResponseEditor__new-title"
      >{{ $t('forum.response.write_a_reply') }}</span
    >
    <ValidationObserver ref="validation-observer">
      <ValidationProvider v-slot="{ failedRules }" name="messageText" rules="required|max:20000" immediate>
        <LLTextarea
          ref="textarea"
          v-model="mentionProcessedText"
          :placeholder="$t('type_here')"
          dynamic-size
          :errors="filteredErrors(failedRules)"
          size="compact"
          menu-bar-style="below"
          :expanded-title="$t('composer_your-reply')"
          :expanded-subtitle="$t('composer_chat-thread')"
          submit-on-mod-enter
          :mention-suggestion-props="{
            chatId: localResponse.chatId
          }"
          :mention-suggestion-component="MentionSuggestion"
          @submit="onSubmitShortcut"
        >
          <template v-if="localResponse.files.length" #bottom-content>
            <LLFileSlider
              class="ForumResponseEditor__files"
              :files.sync="localResponse.files"
              small
              editable
              cropper
              draggable
            ></LLFileSlider>
          </template>
          <template #additional-actions="{ isCapped, expand }">
            <LLFileUploaderWrapper
              file-mime-type="image/*,image/heic,video/*"
              @startUpload="onStartUpload"
              @upload="onUpload"
            >
              <LLButton round tertiary>
                <template #icon-left> <PhotoIcon class="text-status-03-600 w-5 h-5" /> </template>
              </LLButton>
            </LLFileUploaderWrapper>
            <LLUploadProgress
              class="ForumResponseEditor__file-uploader-progress"
              :uploading-files-uuids.sync="uploadingFilesUuids"
            ></LLUploadProgress>
            <LLCheckbox v-model="localResponse.isAnonymous" class="ForumResponseEditor__anonymous">
              <!-- The full label wraps to 2 lines next to the other toolbar icons on mobile,
                   breaking the single-row layout - mobile keeps just "Anonymous", matching
                   the prototype's responsive label - see #1684. -->
              <span class="ForumResponseEditor__anonymous-label ForumResponseEditor__anonymous-label_full">{{
                $t('forum.response.anonymous_reply_checkbox')
              }}</span>
              <span class="ForumResponseEditor__anonymous-label ForumResponseEditor__anonymous-label_short">{{
                $t('forum.response.anonymous_label')
              }}</span>
            </LLCheckbox>
            <span class="ForumResponseEditor__spacer"></span>
            <LLButton v-if="isCapped" class="ForumResponseEditor__expand" small tertiary @click="expand">{{
              $t('button_expand')
            }}</LLButton>
            <LLButton
              v-if="localResponse.id || localResponse.parentResponseId"
              :disabled="isSaving"
              class="ForumResponseEditor__cancel"
              small
              secondary
              @click="cancel"
            >
              {{ $t('button_cancel') }}
            </LLButton>
            <LLButton
              ref="send-button"
              round
              primary
              :loading="isSaving"
              :disabled="!isSendButtonActive"
              :aria-label="localResponse.id ? $t('button_save') : $t('button_send')"
              @click="localResponse.id ? update() : create()"
            >
              <template #icon-left> <SendIcon /> </template>
            </LLButton>
          </template>
        </LLTextarea>
      </ValidationProvider>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import LLFileSlider from '@/components/common/LLFileSlider.vue'
import LLButton from '@/components/common/ui-components/LLButton.vue'
import LLTextarea from '@/components/common/LLTextarea.vue'
import LLFileUploaderWrapper from '@/components/common/LLFileUploader/LLFileUploaderWrapper.vue'
import LLUploadProgress from '@/components/common/LLUploadProgress.vue'
import LLCheckbox from '@/components/common/ui-components/LLCheckbox2.vue'
import PhotoIcon from '@/assets/icons/PhotoIcon.vue'
import SendIcon from '@/assets/icons/SendIcon.vue'
import { ForumResponseModel } from '@/models/forum/forumResponse'
import scrollToForumReplyHelper from '@/utils/modules/forum/scrollToForumReplyHelper'
import MentionSuggestion from '@/components/pages/forum/response/editor/ForumResponseEditorMentionSuggestion.vue'

export default {
  name: 'ForumResponseEditor',
  components: {
    SendIcon,
    PhotoIcon,
    LLCheckbox,
    LLUploadProgress,
    ValidationProvider,
    ValidationObserver,
    LLFileUploaderWrapper,
    LLTextarea,
    LLButton,
    LLFileSlider
  },
  props: {
    response: { type: ForumResponseModel, default: null }
  },
  data() {
    return {
      localResponse: new ForumResponseModel(),
      isSaving: false,
      isValid: false,
      uploadingFilesUuids: [],
      mentinProcessedText: ''
    }
  },
  computed: {
    mentionProcessedText: {
      get() {
        return this.localResponse.mentionProcessedText
      },
      set(text) {
        this.localResponse.mentionProcessedText = text
      }
    },
    sendToServerResponse() {
      console.log('converted', this.localResponse.convertToApi())
      return this.localResponse.convertToApi()
    },
    getFromServerResponse() {
      return ForumResponseModel.parseFromApi({
        response: this.sendToServerResponse,
        chatId: this.localResponse.chatId,
        threadId: this.localResponse.threadId
      })
    },
    MentionSuggestion() {
      return MentionSuggestion
    },
    isSendButtonActive() {
      return this.isValid && !this.uploadingFilesUuids?.length
    }
  },
  watch: {
    localResponse: {
      deep: true,
      immediate: true,
      async handler() {
        this.isValid = await this.validate()
      }
    },
    response: {
      handler() {
        if (!this.$_.isEqual(this.response, this.localResponse)) {
          this.localResponse = new ForumResponseModel(this.response)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(async () => {
      this.isValid = await this.validate()
    })
  },
  methods: {
    filteredErrors(errors) {
      const errorKeys = Object.keys(errors)
      const filteredErrors = errorKeys.filter((key) => key !== 'required').map((key) => errors[key])
      if (filteredErrors?.length) {
        return filteredErrors
      }
      return []
    },
    async validate() {
      const validationObserver = this.$refs['validation-observer']
      if (validationObserver) {
        const validation = await validationObserver.validateWithInfo({ silent: true })
        return validation.flags.valid || !!this.localResponse?.files?.length
      }
      return false
    },
    onStartUpload(uploadingFilesUuids) {
      this.uploadingFilesUuids = uploadingFilesUuids
    },
    onUpload(files) {
      this.localResponse.files = [...this.localResponse.files, ...files]
    },
    // Ctrl/Cmd+Enter in the textarea - routes to whichever action Send currently performs.
    onSubmitShortcut() {
      if (!this.isSendButtonActive || this.isSaving) return
      if (this.localResponse.id) {
        this.update()
      } else {
        this.create()
      }
    },
    async create() {
      this.isSaving = true
      try {
        const { chatId, threadId } = this.localResponse
        const {
          response: serverResponse,
          users,
          prospects
        } = await this.$api.forum.responses.create({
          chatId,
          threadId,
          response: this.localResponse.convertToApi()
        })
        const response = ForumResponseModel.parseFromApi({
          response: serverResponse,
          chatId,
          threadId,
          users,
          prospects
        })
        this.$emit('update', { response })
        scrollToForumReplyHelper.setResponsePath({ responsePath: [response.id] })
      } catch (e) {
        this.$toast.error(this.$getErrorMessage(e))
      } finally {
        this.isSaving = false
      }
    },
    async update() {
      this.isSaving = true
      try {
        const { chatId, threadId } = this.localResponse
        const {
          response: serverResponse,
          users,
          prospects
        } = await this.$api.forum.responses.update({
          chatId,
          threadId,
          response: this.localResponse.convertToApi()
        })
        const response = ForumResponseModel.parseFromApi({
          response: serverResponse,
          chatId,
          threadId,
          users,
          prospects
        })
        this.$emit('update', { response })
        this.cancel()
      } catch (e) {
        this.$toast.error(this.$getErrorMessage(e))
      } finally {
        this.isSaving = false
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    insertText({ text }) {
      if (this.$refs.textarea) {
        this.$refs.textarea.insertText(text)
      }
    },
    insertMention({ prospectId, userId, fullName, isReply, isAnonymous, toModerator, anonymousId }) {
      if (this.$refs.textarea) {
        this.$refs.textarea.insertMention({
          prospectId,
          userId,
          fullName,
          isReply,
          isAnonymous,
          toModerator,
          anonymousId
        })
      }
    },
    focus() {
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus()
        }
        /*this.$nextTick(() => {
          const sendButton = this.$refs['send-button']?.$el
          if (sendButton) {
            sendButton.scrollIntoView({
              block: 'center',
              behavior: 'smooth'
            })
          }
        })*/
      })
    },
    remove() {}
  }
}
</script>

<style scoped lang="scss">
.ForumResponseEditor {
  &__new-title {
    @apply mb-4 text-primary-01-400 text-button-01 block;
  }
  &__anonymous {
    @apply ml-2;
  }
  &__anonymous-label {
    @apply text-caption-01 text-neutral-01-400 whitespace-nowrap;
    &_full {
      @apply hidden;
      @screen sm {
        @apply inline;
      }
    }
    &_short {
      @screen sm {
        @apply hidden;
      }
    }
  }
  &__file-uploader-progress {
    @apply ml-2;
  }
  &__spacer {
    @apply flex-1;
  }
  &__cancel {
    @apply mr-2;
  }
  &__expand {
    // "Expand" opens the mobile fullscreen editor - desktop uses the drag-resize handle
    // instead, see #1684 requirement 9.
    @apply mr-1;
    @screen sm {
      @apply hidden;
    }
  }
  &__files {
    @apply mt-2;
  }
}
</style>
